import { MixpanelConsumer } from 'react-mixpanel';
import { forwardRef } from 'react';
import memoize from 'lodash/memoize';
import unconf_mixpanel from 'mixpanel-browser';

const noop = () => {};
const noopMixpanel = {
    track: noop,
    track_links: noop,
    identify: noop,
    reset: noop,
    people: {
        set: noop,
    },
};

const { MIXPANEL_TOKEN } = window.spate_env || {};

const conf_mixpanel = memoize((token) => {
    if (!token) {
        console.warn('Mixpanel token missing; using noop mixpanel');
        return noopMixpanel;
    }
    unconf_mixpanel.init(token, {
        opt_out_tracking_by_default: true,
    });
    return unconf_mixpanel;
})(MIXPANEL_TOKEN);

export default conf_mixpanel;
const globalScope = typeof globalThis !== 'undefined' ? globalThis : window;
globalScope.mixpanel = conf_mixpanel;

const mixpanelCall =
    (property) =>
    (...args) => {
        if (!conf_mixpanel) {
            console.warn('Mixpanel not configured? mixpanelTrack exiting');
            return;
        }
        conf_mixpanel[property](...args);
    };

export const mixpanelTrack = mixpanelCall('track');
export const mixpanelTrackLinks = mixpanelCall('track_links');

export const withMixpanelChange = withMixpanelEvent.bind(
    null,
    'Change',
    'onChange',
);
export const withMixpanelClick = withMixpanelEvent.bind(
    null,
    'Click',
    'onClick',
);

export function withMixpanelEvent(
    mixpanelEventName,
    handlerKey,
    Component,
    context,
) {
    const MixpanelEventWrapper = forwardRef(
        (
            {
                mixpanelMeta,
                handleMixpanelEvent,
                [handlerKey]: handler,
                ...restProps
            },
            ref,
        ) => {
            const mixpanelContext = restProps.mixpanelContext || context;

            return (
                <Component
                    {...{
                        [handlerKey]: wrapHandler({
                            mixpanelContext,
                            mixpanelMeta,
                            mixpanelEventName,
                            handleMixpanelEvent,
                            handler,
                        }),
                    }}
                    {...restProps}
                    ref={ref}
                />
            );
        },
    );

    return MixpanelEventWrapper;
}

function wrapHandler({
    mixpanelContext,
    mixpanelMeta = {},
    mixpanelEventName,
    handleMixpanelEvent,
    handler,
}) {
    const wrapped = (...args) => {
        unconf_mixpanel.track(mixpanelEventName, {
            ...mixpanelMeta,
            ...(typeof handleMixpanelEvent === 'function' &&
                handleMixpanelEvent(...args)),
            context: mixpanelContext,
        });
    };

    if (typeof handler !== 'function') {
        return wrapped;
    }

    return (...args) => {
        wrapped(...args);
        handler(...args);
    };
}

export const withMixpanel = (Component) => {
    return function (props) {
        return (
            <MixpanelConsumer>
                {(mixpanel) => {
                    if (!mixpanel)
                        bugsnagClient.notify(new Error('Mixpanel not found'));
                    return <Component {...props} mixpanel={mixpanel} />;
                }}
            </MixpanelConsumer>
        );
    };
};
